import React from 'react';
import { Typography, Stack, Box } from '@mui/material';
import SEOHead from './SEO/SEOHead';
import { styled } from '@mui/material/styles';
import ResponsiveContainer from '../components/layout/ResponsiveContainer';
import SectionTitle from './shared/SectionTitle';

const PageWrapper = styled('div')(({ theme }) => ({
    width: '100%',
    paddingBottom: theme.spacing(6)
}));

const HeroSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a2e4a 0%, #2a4a6e 100%)',
    color: '#ffffff',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='%23ffffff' fill-opacity='0.02'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px',
    },
}));

const SailImage = styled('img')(({ theme }) => ({
    width: '300px',
    maxWidth: '100%',
    height: 'auto',
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
        width: '100%'
    }
}));

const SailPicture = styled('picture')({});

const FeatureList = styled('ul')(({ theme }) => ({
    listStyle: 'none',
    padding: 0,
    margin: 0,
    '& li': {
        padding: '4px 0',
        position: 'relative',
        paddingLeft: '20px',
        '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: theme.palette.orange.main
        }
    }
}));

interface SailTypeProps {
    title: string;
    image: string;
    imageWebp: string;
    imageWidth: number;
    imageHeight: number;
    standardFeatures: string[];
    optionalFeatures: string[];
}

const SailTypeSection: React.FC<SailTypeProps> = ({
    title,
    image,
    imageWebp,
    imageWidth,
    imageHeight,
    standardFeatures,
    optionalFeatures
}) => (
    <Box>
        <Typography variant="h4" align="center" sx={{ mb: 2 }}>
            {title}
        </Typography>
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 },
            alignItems: 'flex-start'
        }}>
            <Box sx={{ flexShrink: 0 }}>
                <SailPicture>
                    <source srcSet={imageWebp} type="image/webp" />
                    <SailImage
                        src={image}
                        alt={title}
                        width={imageWidth}
                        height={imageHeight}
                        loading="lazy"
                        decoding="async"
                    />
                </SailPicture>
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 4 },
                flex: 1
            }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Standard Features:
                    </Typography>
                    <FeatureList>
                        {standardFeatures.map((feature, index) => (
                            <li key={index}>{feature}</li>
                        ))}
                    </FeatureList>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Optional:
                    </Typography>
                    <FeatureList>
                        {optionalFeatures.map((feature, index) => (
                            <li key={index}>{feature}</li>
                        ))}
                    </FeatureList>
                </Box>
            </Box>
        </Box>
    </Box>
);

const CruisingSailsPage = () => {
    const mainsails = {
        title: 'Mainsails',
        image: '/images/CruisingSails_1.jpg',
        imageWebp: '/images/CruisingSails_1.webp',
        imageWidth: 800,
        imageHeight: 1200,
        standardFeatures: [
            'Reef Point',
            'Top Full Batten',
            'Loose Foot',
            'Leech and Foot Lines',
            'Aluminum Headboard',
            'Stainless Steel Press Rings',
            'Tell-tales',
        ],
        optionalFeatures: [
            'Luff Slides (no additional cost)',
            'Full Battens',
            'Additional Reefs',
            'Draft Stripes',
            'Offshore Construction',
        ],
    };

    const headsails = {
        title: 'Headsails',
        image: '/images/CruisingSails_2.jpg',
        imageWebp: '/images/CruisingSails_2.webp',
        imageWidth: 800,
        imageHeight: 1200,
        standardFeatures: [
            'Luff Tape',
            'Leech and Foot Lines',
            'Webbing on the Head and Tack',
            'Stainless Press Ring on the Clew',
            'Tell-tales',
        ],
        optionalFeatures: [
            'Hanks',
            '100% Acrylic UV on Leech and Foot',
            'Foam Luff',
            'Draft Stripes',
            'Offshore Construction',
        ],
    };

    return (
        <PageWrapper>
            <SEOHead page="cruisingSails" />
            
            {/* Navy Hero Section */}
            <HeroSection>
                <Box sx={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            fontWeight: 500,
                            mb: 2,
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            color: '#ffffff',
                        }}
                    >
                        Crosscut Sails
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: '1.15rem',
                            fontWeight: 300,
                            opacity: 0.9,
                            maxWidth: '600px',
                            margin: '0 auto',
                            color: '#ffffff',
                        }}
                    >
                        Crosscut Designs for Those Looking for Quality and Value
                    </Typography>
                </Box>
            </HeroSection>

            <ResponsiveContainer maxWidth="lg" sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
                    <SailTypeSection {...mainsails} />
                    <SailTypeSection {...headsails} />

                    <Box>
                        <SectionTitle sx={{ mb: 1 }}>
                            Available in:
                        </SectionTitle>
                        <FeatureList>
                            <li>Challenge Performance Cruise</li>
                            <li>Challenge High Mass Warp</li>
                            <li>Challenge High Modulus</li>
                        </FeatureList>
                    </Box>
                </Box>
            </ResponsiveContainer>
        </PageWrapper>
    );
};

export default CruisingSailsPage;
